/**
 * Utility functions for ChatSidebar component
 * These are pure functions with no external dependencies
 */

/**
 * Generates contextual chat input placeholders based on current context
 * @param {Object} currentContext - The current context object
 * @param {boolean} tourInProgress - Whether a tour is currently running
 * @param {boolean} isTyping - Whether the agent is currently typing
 * @returns {string} - Contextual placeholder text
 */
export const getContextualPlaceholder = (currentContext, tourInProgress, isTyping) => {
  // Override for special states
  if (tourInProgress) return "Analyzing your data...";
  if (isTyping) return "Processing your request...";
  
  // Default fallback
  if (!currentContext) return "Ask about your SEO...";
  
  const { type, route, subType, title } = currentContext;
  
  // Page analysis context
  if (type === 'page_analysis') {
    return "Ask about this page's performance...";
  }
  
  // Report contexts
  if (type === 'analysis_view') {
    switch (route) {
      case 'keywords':
        return "Ask about these keywords...";
      case 'content-gaps':
        return "Ask about content opportunities...";
      case 'page-quality':
        return "Ask about page quality issues...";
      case 'technical-seo':
        return "Ask about technical SEO...";
      case 'serp-features':
        return "Ask about SERP features...";
      case 'search-intent':
        return "Ask about search intent...";
      case 'overview':
        return "Ask about this analysis...";
      default:
        return "Ask about this report...";
    }
  }
  
  // Specific page context
  if (currentContext.page || currentContext.url) {
    return "Ask about this page...";
  }
  
  // Generic analysis context
  if (type === 'analysis' || route) {
    return "Ask about this analysis...";
  }
  
  // Fallback
  return "Ask about your SEO...";
};

/**
 * Determines if a message content should be auto-expanded based on length and structure
 * @param {string} content - The message content to analyze
 * @returns {boolean} - Whether the message should be auto-expanded
 */
export const shouldAutoExpand = (content) => {
  if (!content) return false;
  
  // Check for long content (>1500 characters)
  if (content.length > 1500) return true;
  
  // Check for multiple sections (more than 3 headings)
  const headingCount = (content.match(/^#{1,3}\s/gm) || []).length;
  if (headingCount > 3) return true;
  
  // Check for long lists (more than 8 list items)
  const listItems = (content.match(/^[-*+]\s/gm) || []).length;
  if (listItems > 8) return true;
  
  // Check for tables
  const tableCount = (content.match(/\|.*\|/g) || []).length;
  if (tableCount > 6) return true; // More than 6 table rows
  
  return false;
};

/**
 * Maps route paths to report types for navigation
 * @param {string} route - The route path (e.g., '/keywords')
 * @returns {string} - The corresponding report type
 */
export const getReportTypeFromRoute = (route) => {
  const routeMapping = {
    '/keywords': 'keywords',
    '/pages': 'pages',
    '/lighthouse': 'lighthouse'
  };
  
  return routeMapping[route] || 'keywords';
};

/**
 * Route mapping object for choice card navigation
 * Used by handleChoiceCardSelect to map routes to report types
 */
export const ROUTE_MAPPING = {
  '/keywords': 'keywords',
  '/pages': 'pages',
  '/lighthouse': 'lighthouse'
};

/**
 * Creates simple task flow handlers for ChatSidebar
 * These are lightweight handlers that mainly log events and update state
 * @param {Function} setIsTaskFlowActive - State setter for task flow active state
 * @param {Function} setActiveTaskCard - State setter for active task card
 * @returns {Object} - Object containing all task flow handlers
 */
export const createTaskFlowHandlers = (setIsTaskFlowActive, setActiveTaskCard) => ({
  handleTaskGenerated: (task) => {
    // Task generated successfully
  },

  handleStepComplete: (stepIndex, isCompleted) => {
    // Step completion handled
  },

  handleTaskComplete: () => {
    // Task completed successfully
  },

  handleTaskClose: () => {
    setIsTaskFlowActive(false);
    setActiveTaskCard(null);
  }
});

/**
 * Creates simple modal handlers for ChatSidebar
 * These handle opening/closing various modal states
 * @param {Function} setExpandedMessage - State setter for expanded message
 * @param {Function} setIsExpandedMessageModalOpen - State setter for expanded message modal
 * @param {Function} setIsChoiceCardsModalOpen - State setter for choice cards modal
 * @param {Function} setChoiceCardsData - State setter for choice cards data
 * @param {boolean} portalMode - Whether in portal mode (disables message expansion)
 * @returns {Object} - Object containing all modal handlers
 */
export const createModalHandlers = (
  setExpandedMessage,
  setIsExpandedMessageModalOpen,
  setIsChoiceCardsModalOpen,
  setChoiceCardsData,
  portalMode = false
) => ({
  handleExpandMessage: portalMode ? null : (message) => {
    setExpandedMessage(message);
    setIsExpandedMessageModalOpen(true);
  },

  handleCloseExpandedMessage: () => {
    setIsExpandedMessageModalOpen(false);
    setExpandedMessage(null);
  },

  handleChoiceCardsClose: () => {
    setIsChoiceCardsModalOpen(false);
    setChoiceCardsData(null);
  }
});
